import React from "react";
import { Box, Skeleton } from "@mui/material";
import {
  AVATAR_RADIUS,
  AVATAR_SIZE,
  TEXT_INDENT,
} from "./conversationConstants";
import { messageRowSx } from "./conversationUtils";
import { pageHeaderMinSx } from "../../../constants/pageLayout";

const skeletonWave = { animation: "wave" };

const MESSAGE_PLACEHOLDERS = [
  {
    lines: ["78%", "62%"],
  },
  {
    lines: ["88%", "74%", "48%"],
    groupedLines: ["56%"],
  },
  {
    lines: ["52%"],
  },
  {
    lines: ["84%", "70%", "58%", "36%"],
  },
];

function MessageLineSkeletons({ lines }) {
  return lines.map((width, index) => (
    <Skeleton
      key={`${width}-${index}`}
      variant="text"
      animation="wave"
      sx={{
        ...skeletonWave,
        width,
        height: 16,
        maxWidth: "100%",
        borderRadius: "4px",
        transform: "none",
        mt: index === 0 ? 0.25 : 0.5,
      }}
    />
  ));
}

function MessageRowSkeleton({ lines, groupedLines = [] }) {
  return (
    <>
      <Box sx={{ ...messageRowSx, pt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
          <Skeleton
            variant="rounded"
            animation="wave"
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            sx={{
              ...skeletonWave,
              borderRadius: AVATAR_RADIUS,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                mb: 0.25,
              }}
            >
              <Skeleton
                variant="text"
                animation="wave"
                width={72}
                height={18}
                sx={{ ...skeletonWave, transform: "none" }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                width={44}
                height={14}
                sx={{ ...skeletonWave, transform: "none" }}
              />
            </Box>
            <MessageLineSkeletons lines={lines} />
          </Box>
        </Box>
      </Box>
      {groupedLines.map((width, index) => (
        <Box key={`grouped-${width}-${index}`} sx={{ ...messageRowSx, py: 0.4 }}>
          <Box sx={{ pl: TEXT_INDENT }}>
            <Skeleton
              variant="text"
              animation="wave"
              width={width}
              height={16}
              sx={{
                ...skeletonWave,
                maxWidth: "100%",
                borderRadius: "4px",
                transform: "none",
              }}
            />
          </Box>
        </Box>
      ))}
    </>
  );
}

function ChatHeaderSkeleton() {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.subtle",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        ...pageHeaderMinSx,
        boxSizing: "border-box",
      }}
    >
      <Skeleton
        variant="rounded"
        animation="wave"
        width={88}
        height={22}
        sx={{ ...skeletonWave, borderRadius: "6px" }}
      />
      <Skeleton
        variant="rounded"
        animation="wave"
        width={72}
        height={20}
        sx={{ ...skeletonWave, borderRadius: "999px" }}
      />
      <Box sx={{ flex: 1 }} />
      <Skeleton
        variant="circular"
        animation="wave"
        width={28}
        height={28}
        sx={skeletonWave}
      />
    </Box>
  );
}

function ComposerSkeleton() {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          px: 1.5,
          py: 1.25,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          backgroundColor: "background.paper",
        }}
      >
        <Skeleton
          variant="circular"
          animation="wave"
          width={28}
          height={28}
          sx={{ ...skeletonWave, flexShrink: 0, mb: 0.25 }}
        />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Skeleton
            variant="text"
            animation="wave"
            width="72%"
            height={16}
            sx={{ ...skeletonWave, transform: "none" }}
          />
          <Skeleton
            variant="text"
            animation="wave"
            width="38%"
            height={14}
            sx={{ ...skeletonWave, transform: "none" }}
          />
        </Box>
        <Skeleton
          variant="circular"
          animation="wave"
          width={28}
          height={28}
          sx={{ ...skeletonWave, flexShrink: 0, mb: 0.25 }}
        />
      </Box>
    </Box>
  );
}

function ConversationMessagesSkeleton({
  hideHeader = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {!hideHeader && <ChatHeaderSkeleton />}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          py: 1.5,
        }}
      >
        {MESSAGE_PLACEHOLDERS.map((placeholder, index) => (
          <MessageRowSkeleton key={index} {...placeholder} />
        ))}
      </Box>
      <ComposerSkeleton />
    </Box>
  );
}

export default ConversationMessagesSkeleton;
